import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, uploadUrl } from "@/lib/api";
import { buildWhatsAppUrl } from "@/lib/whatsapp";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, MessageSquare, Copy, Pencil } from "lucide-react";
import AdminEditMemberDialog from "./AdminEditMemberDialog";

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatDDMon(value) {
    if (!value) return "";
    // Expected input: YYYY-MM-DD (from <input type="date">)
    const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
        const monthIdx = parseInt(m[2], 10) - 1;
        const day = m[3];
        if (monthIdx >= 0 && monthIdx < 12) return `${day}-${MONTHS_SHORT[monthIdx]}`;
    }
    // Fallback: try Date parsing
    const d = new Date(value);
    if (!isNaN(d.getTime())) {
        const dd = String(d.getDate()).padStart(2, "0");
        return `${dd}-${MONTHS_SHORT[d.getMonth()]}`;
    }
    return String(value);
}

export default function AdminMemberProfile() {
    const { token } = useParams();
    const [m, setM] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [editOpen, setEditOpen] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/admin/members/${token}`);
                setM(data);
            } catch (err) {
                toast.error("Failed to load member");
            } finally {
                setLoading(false);
            }
        })();
    }, [token]);

    const sendReminder = async () => {
        setSending(true);
        try {
            const { data } = await api.post(`/admin/members/${token}/reminder`);
            if (data.ok) toast.success("SMS sent");
            else toast("Reminder queued", { description: data.detail });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        } finally {
            setSending(false);
        }
    };

    const copyLink = async () => {
        const link = `${window.location.origin}/form/${token}`;
        try {
            await navigator.clipboard.writeText(link);
            toast.success("Personal form link copied");
        } catch {
            toast.error("Could not copy link");
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen grid place-items-center text-muted-foreground" data-testid="profile-loading">
                Loading…
            </div>
        );
    }
    if (!m) return null;

    return (
        <div className="min-h-screen bg-background" data-testid="admin-profile-page">
            <header className="sticky top-0 z-30 glass-panel">
                <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
                    <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-rotary-blue hover:underline">
                        <ArrowLeft className="h-4 w-4" /> Back to directory
                    </Link>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setEditOpen(true)}
                            className="rounded-full border-rotary-blue text-rotary-blue hover:bg-rotary-blue hover:text-white"
                            data-testid="profile-edit-btn"
                        >
                            <Pencil className="mr-1.5 h-4 w-4" /> Edit
                        </Button>
                        <Button
                            variant="outline"
                            onClick={copyLink}
                            className="rounded-full"
                            data-testid="copy-link-btn"
                        >
                            <Copy className="mr-1.5 h-4 w-4" /> Copy link
                        </Button>
                        <Button
                            onClick={sendReminder}
                            disabled={sending}
                            className="rounded-full bg-rotary-gold text-rotary-ink hover:bg-rotary-gold/85"
                            data-testid="profile-send-reminder-btn"
                        >
                            <MessageSquare className="mr-1.5 h-4 w-4" /> {sending ? "Sending…" : "Send SMS"}
                        </Button>
                        <a
                            href={buildWhatsAppUrl(m.name, m.phone, m.token, m.progress)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] text-white px-4 py-2 text-sm font-medium hover:bg-[#1ebe57]"
                            data-testid="profile-send-whatsapp-btn"
                        >
                            <WhatsAppIcon className="h-4 w-4" /> WhatsApp
                        </a>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-5xl px-6 py-10 space-y-8">
                {/* Hero */}
                <section className="rounded-2xl border bg-white p-8">
                    <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
                        <PhotoTile filename={m.photo_filename} name={m.name} version={m.updated_at} large />
                        <div className="flex-1 space-y-3">
                            <span className="label-eyebrow">Member profile</span>
                            <h1 className="rotary-heading text-4xl text-rotary-ink" data-testid="profile-name">
                                {m.name}
                            </h1>
                            <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                                {(m.role_display || m.position) && (
                                    <span
                                        className="rotary-chip bg-rotary-blue/5 border-rotary-blue/20 text-rotary-blue"
                                        data-testid="profile-role-chip"
                                    >
                                        {m.role_display || m.position}
                                    </span>
                                )}
                                {m.club_name && <span className="rotary-chip">{m.club_name}</span>}
                                <span
                                    className={`rotary-chip ${
                                        m.submitted
                                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                            : "bg-rotary-gold/10 text-rotary-ink border-rotary-gold/40"
                                    }`}
                                >
                    {m.submitted ? "Submitted" : ""}
                                </span>
                            </div>
                            <div className="max-w-md space-y-1.5 pt-2">
                                <div className="flex items-center justify-between text-[11px]">
                                    <span className="label-eyebrow">Profile completion</span>
                                    <span className="font-semibold text-rotary-ink">{m.progress}%</span>
                                </div>
                                <Progress value={m.progress} className="h-2 bg-muted [&>div]:rotary-progress-fill" />
                            </div>
                        </div>
                    </div>
                </section>

                {/* Details grid */}
                <section className="grid gap-4 md:grid-cols-2">
                    <DetailRow label="Name" value={m.name} testid="profile-name-row" />
                    <DetailRow label="Phone" value={m.phone} testid="profile-phone" />
                    <DetailRow label="Email" value={m.email} testid="profile-email" />
                    <DetailRow label="Club name" value={m.club_name} testid="profile-club-name" />
                    <DetailRow label="Position" value={m.role_display || m.position} testid="profile-position" />
                    <DetailRow label="Classification" value={m.classification} testid="profile-classification" />
                    <DetailRow label="RI District" value={m.ri_district} testid="profile-ri-district" />
                    <DetailRow label="Birthday" value={formatDDMon(m.birthday)} testid="profile-birthday" />
                    <div className="md:col-span-2">
                        <DetailRow label="Communication address" value={m.postal_address} testid="profile-address" />
                    </div>
                </section>

                {/* Spouse - always shown */}
                <section className="rounded-2xl border bg-white p-6">
                    <span className="label-eyebrow">Spouse details</span>
                    <div className="mt-4 grid gap-6 sm:grid-cols-[auto_1fr]">
                        <PhotoTile filename={m.spouse_photo_filename} name={m.spouse_name || "Spouse"} version={m.updated_at} />
                        <div className="grid gap-4 sm:grid-cols-2">
                            <DetailRow label="Spouse name" value={m.spouse_name} testid="profile-spouse-name" />
                            <DetailRow label="Spouse birthday" value={formatDDMon(m.spouse_birthday)} testid="profile-spouse-birthday" />
                            <div className="sm:col-span-2">
                                <DetailRow label="Anniversary" value={formatDDMon(m.anniversary)} testid="profile-anniversary" />
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <AdminEditMemberDialog
                open={editOpen}
                onOpenChange={setEditOpen}
                member={m}
                onSaved={(data) => setM(data)}
            />
        </div>
    );
}

function DetailRow({ label, value, testid }) {
    return (
        <div className="rounded-xl border bg-white p-4">
            <span className="label-eyebrow">{label}</span>
            <p className="mt-1.5 text-base text-rotary-ink" data-testid={testid}>
                {value ? value : <span className="text-muted-foreground italic">Not provided</span>}
            </p>
        </div>
    );
}

function PhotoTile({ filename, name, large, version }) {
    const src = filename ? uploadUrl(filename, version) : "";
    const size = large ? "h-32 w-32" : "h-24 w-24";
    const initials = (name || "?")
        .split(" ")
        .map((s) => s[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase();
    if (!src) {
        return (
            <div
                className={`${size} shrink-0 grid place-items-center rounded-2xl bg-rotary-blue text-white rotary-heading text-3xl`}
                data-testid="photo-placeholder"
            >
                {initials || "?"}
            </div>
        );
    }
    return (
        <img
            src={src}
            alt={name}
            className={`${size} shrink-0 rounded-2xl object-cover border`}
            data-testid="photo-image"
        />
    );
}
